/**
 * memberships.service.ts
 *
 * Typed API call wrappers for the Memberships domain.
 * All methods use the shared apiClient (Axios instance with JWT interceptor).
 */
import { apiClient } from './apiClient';

export type BillingInterval = 'monthly' | 'annual' | 'one-time';
export type EnrollmentStatus = 'active' | 'cancelled' | 'expired' | 'pending' | 'paused';
export type ItemType = 'procedure' | 'product';

export interface MembershipProcedure {
  templateProcedureId: number;
  templateId: number;
  procedureId: number;
  procedureName: string | null;
  standardPrice: number;
  effectivePrice: number;
}

export interface MembershipProduct {
  templateProductId: number;
  templateId: number;
  name: string;
  quantity: number;
  standardPrice: number;
  effectivePrice: number;
}

export interface MembershipTemplate {
  templateId: number;
  accountId: number;
  title: string;
  description: string | null;
  price: number;
  billingInterval: BillingInterval;
  active: boolean;
  createdAt: string;
  updatedAt: string;
  procedures?: MembershipProcedure[];
  products?: MembershipProduct[];
}

export interface CreateTemplatePayload {
  title: string;
  description?: string | null;
  price: number;
  billingInterval: BillingInterval;
  active?: boolean;
}

export interface UpdateTemplatePayload {
  title?: string;
  description?: string | null;
  price?: number;
  billingInterval?: BillingInterval;
  active?: boolean;
}

export interface AddProcedurePayload {
  procedureId: number;
  standardPrice: number;
  effectivePrice: number;
}

export interface AddProductPayload {
  name: string;
  quantity: number;
  standardPrice: number;
  effectivePrice: number;
}

export interface MembershipItemUsage {
  itemUsageId: number;
  enrollmentId: number;
  itemType: ItemType;
  itemId: number;
  name: string;
  quantity: number;
  quantityUsed: number;
  quantityRemaining: number;
  effectivePrice: number;
  itemState?: string;
  statusText?: string;
  usedDate?: string | null;
  expireDate?: string | null;
  deleteDate?: string | null;
  canUse?: boolean;
  canReturn?: boolean;
  canDelete?: boolean;
}

export interface MembershipEnrollment {
  enrollmentId: number;
  accountId: number;
  patientId: number;
  templateId: number;
  templateTitle: string | null;
  patientName?: string | null;
  status: EnrollmentStatus;
  startDate: string;
  endDate: string | null;
  expireDate: string | null;
  pauseUntilDate: string | null;
  membershipTotal: number | null;
  cancelledAt: string | null;
  cancellationReasonId: number | null;
  recurringPaymentId: number | null;
  createdAt: string;
  updatedAt: string;
  procedureItems?: MembershipItemUsage[];
  productItems?: MembershipItemUsage[];
  recurringAmount?: number | null;
  payType?: string | null;
  payMethod?: string | null;
  recurringPlanId?: number | null;
  recurringPlanDisplayId?: string | null;
  recurringQuoteId?: number | null;
  nextBillingDate?: string | null;
  paymentStatus?: string | null;
}

export interface CancellationReason {
  reasonId: number;
  accountId: number;
  reason: string;
  active: boolean;
}

type RawRecord = Record<string, unknown>;

function asNumber(value: unknown, fallback = 0): number {
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  if (typeof value === 'string' && value.trim() !== '' && !Number.isNaN(Number(value))) {
    return Number(value);
  }
  return fallback;
}

function asString(value: unknown, fallback = ''): string {
  return typeof value === 'string' ? value : fallback;
}

function asNullableString(value: unknown): string | null {
  return typeof value === 'string' && value !== '' ? value : null;
}

function asNullableScalarString(value: unknown): string | null {
  if (value == null) return null;
  const parsed = String(value).trim();
  return parsed === '' ? null : parsed;
}

function isZeroDate(value: unknown): boolean {
  return value == null || value === '' || value === '0000-00-00' || value === '0000-00-00 00:00:00';
}

function intervalFromIncrementType(value: unknown): BillingInterval {
  return asNumber(value, 1) === 0 ? 'one-time' : 'monthly';
}

function statusFromEnrollment(raw: RawRecord): EnrollmentStatus {
  const endDate = raw.endDate;
  const pauseUntilDate = raw.pauseUntilDate;

  if (!isZeroDate(pauseUntilDate)) return 'paused';
  if (isZeroDate(endDate)) return 'active';
  return 'cancelled';
}

function mapProcedure(raw: RawRecord, templateId: number): MembershipProcedure {
  return {
    templateProcedureId: asNumber(raw.templateProcedureId ?? raw.membershipTemplateProcedureID),
    templateId,
    procedureId: asNumber(raw.procedureId ?? raw.procSubID ?? raw.procID),
    procedureName: asNullableString(raw.procedureName ?? raw.name),
    standardPrice: asNumber(raw.standardPrice ?? raw.fee ?? raw.price),
    effectivePrice: asNumber(raw.effectivePrice ?? raw.price),
  };
}

function mapProduct(raw: RawRecord, templateId: number): MembershipProduct {
  return {
    templateProductId: asNumber(raw.templateProductId ?? raw.membershipTemplateProductID),
    templateId,
    name: asString(raw.name),
    quantity: asNumber(raw.quantity, 1),
    standardPrice: asNumber(raw.standardPrice ?? raw.curRate ?? raw.fee ?? raw.price),
    effectivePrice: asNumber(raw.effectivePrice ?? raw.price ?? raw.total),
  };
}

function mapTemplate(raw: RawRecord): MembershipTemplate {
  const templateId = asNumber(raw.templateId ?? raw.membershipTemplateID);
  const createdAt = asString(raw.createdAt ?? raw.createDate);

  return {
    templateId,
    accountId: asNumber(raw.accountId ?? raw.accountID),
    title: asString(raw.title),
    description: asNullableString(raw.description),
    price: asNumber(raw.price ?? raw.membershipTotal ?? raw.amount),
    billingInterval: typeof raw.billingInterval === 'string'
      ? (raw.billingInterval as BillingInterval)
      : intervalFromIncrementType(raw.incrementType),
    active: typeof raw.active === 'boolean' ? raw.active : raw.status === 'active',
    createdAt,
    updatedAt: asString(raw.updatedAt ?? raw.createDate ?? raw.updateDate, createdAt),
    procedures: Array.isArray(raw.procedures)
      ? raw.procedures.map((item) => mapProcedure(item as RawRecord, templateId))
      : Array.isArray(raw.procedureItems)
        ? (raw.procedureItems as RawRecord[]).map((item, i) => ({
            templateProcedureId: i,
            templateId,
            procedureId: 0,
            procedureName: asString(item.name),
            standardPrice: 0,
            effectivePrice: 0,
          }))
        : undefined,
    products: Array.isArray(raw.products)
      ? raw.products.map((item) => mapProduct(item as RawRecord, templateId))
      : Array.isArray(raw.productItems)
        ? (raw.productItems as RawRecord[]).map((item, i) => ({
            templateProductId: i,
            templateId,
            name: asString(item.name),
            quantity: asNumber(item.quantity, 1),
            standardPrice: 0,
            effectivePrice: 0,
          }))
        : undefined,
  };
}

function flattenTemplatePayload(payload: unknown): RawRecord[] {
  if (!Array.isArray(payload)) return [];

  const rows: RawRecord[] = [];
  for (const entry of payload) {
    const record = entry as RawRecord;
    if (Array.isArray(record.templates)) {
      for (const template of record.templates as RawRecord[]) {
        rows.push(template);
      }
      continue;
    }
    rows.push(record);
  }
  return rows;
}

function mapItemUsage(raw: RawRecord, enrollmentId: number, itemType: ItemType): MembershipItemUsage {
  const quantity = asNumber(raw.quantity, 1);
  const quantityUsed = asNumber(raw.quantityUsed ?? raw.quantity_used ?? 0);
  const quantityRemaining = asNumber(raw.quantityRemaining ?? raw.quantity_remaining ?? Math.max(quantity - quantityUsed, 0));
  const usedDate = isZeroDate(raw.usedDate) ? null : asNullableString(raw.usedDate);
  const expireDate = isZeroDate(raw.expireDate) ? null : asNullableString(raw.expireDate);
  const deleteDate = isZeroDate(raw.deleteDate) ? null : asNullableString(raw.deleteDate);

  return {
    itemUsageId: asNumber(raw.itemUsageId ?? raw.membershipProcedureID ?? raw.membershipProductID),
    enrollmentId,
    itemType,
    itemId: asNumber(raw.itemId ?? raw.procedureId ?? raw.productId ?? raw.procSubID ?? raw.productID),
    name: asString(raw.name ?? raw.title ?? raw.strItem),
    quantity,
    quantityUsed,
    quantityRemaining,
    effectivePrice: asNumber(raw.effectivePrice ?? raw.price ?? raw.total),
    itemState: asString(raw.itemState, quantityRemaining > 0 ? 'available' : 'used'),
    statusText: asString(raw.statusText),
    usedDate,
    expireDate,
    deleteDate,
    canUse: raw.canUse == null ? quantityRemaining > 0 : Boolean(raw.canUse),
    canReturn: raw.canReturn == null ? quantityUsed > 0 : Boolean(raw.canReturn),
    canDelete: raw.canDelete == null ? deleteDate == null : Boolean(raw.canDelete),
  };
}

function mapMembershipBlock(raw: RawRecord): EnrollmentMembershipItem {
  const membershipId = asNumber(raw.membershipId);
  const rawTemplateId = asNumber(raw.templateId ?? raw.membershipTemplateId ?? raw.membershipTemplateID);
  return {
    membershipId,
    templateId: rawTemplateId > 0 ? rawTemplateId : null,
    title: asString(raw.title),
    procedureItems: Array.isArray(raw.procedureItems)
      ? (raw.procedureItems as RawRecord[]).map((item) => mapItemUsage(item, membershipId, 'procedure'))
      : [],
    productItems: Array.isArray(raw.productItems)
      ? (raw.productItems as RawRecord[]).map((item) => mapItemUsage(item, membershipId, 'product'))
      : [],
  };
}

function mapEnrollment(raw: RawRecord): MembershipEnrollment {
  const enrollmentId = asNumber(raw.enrollmentId ?? raw.membershipId ?? raw.membershipID);
  const createdAt = asString(raw.createdAt ?? raw.createDate);
  return {
    enrollmentId,
    accountId: asNumber(raw.accountId ?? raw.accountID),
    patientId: asNumber(raw.patientId ?? raw.patientID),
    templateId: asNumber(raw.templateId ?? raw.membershipTemplateId ?? raw.membershipTemplateID),
    templateTitle: asNullableString(raw.templateTitle ?? raw.title),
    patientName: asNullableString(raw.patientName),
    status: (raw.status as EnrollmentStatus) ?? statusFromEnrollment(raw),
    startDate: asString(raw.startDate ?? raw.createDate),
    endDate: isZeroDate(raw.endDate) ? null : asNullableString(raw.endDate),
    expireDate: isZeroDate(raw.expireDate) ? null : asNullableString(raw.expireDate),
    pauseUntilDate: isZeroDate(raw.pauseUntilDate) ? null : asNullableString(raw.pauseUntilDate),
    membershipTotal: raw.membershipTotal == null ? null : asNumber(raw.membershipTotal),
    cancelledAt: isZeroDate(raw.cancelledAt ?? raw.endDate) ? null : asNullableString(raw.cancelledAt ?? raw.endDate),
    cancellationReasonId: raw.cancellationReasonId == null ? null : asNumber(raw.cancellationReasonId),
    recurringPaymentId: raw.recurringPaymentId == null
      ? (raw.paymentsRecurringAuthnetID == null ? null : asNumber(raw.paymentsRecurringAuthnetID))
      : asNumber(raw.recurringPaymentId),
    createdAt,
    updatedAt: asString(raw.updatedAt ?? raw.createDate ?? raw.updateDate, createdAt),
    procedureItems: Array.isArray(raw.procedureItems)
      ? raw.procedureItems.map((item) => mapItemUsage(item as RawRecord, enrollmentId, 'procedure'))
      : undefined,
    productItems: Array.isArray(raw.productItems)
      ? raw.productItems.map((item) => mapItemUsage(item as RawRecord, enrollmentId, 'product'))
      : undefined,
    recurringAmount: raw.recurringAmount != null ? asNumber(raw.recurringAmount) : null,
    payType: asNullableString(raw.payType),
    payMethod: asNullableString(raw.payMethod),
    recurringPlanId: raw.recurringPlanId != null ? asNumber(raw.recurringPlanId) : (raw.planID != null ? asNumber(raw.planID) : null),
    recurringPlanDisplayId: asNullableString(raw.recurringPlanDisplayId ?? raw.planDisplayID),
    recurringQuoteId: raw.recurringQuoteId != null ? asNumber(raw.recurringQuoteId) : (raw.quoteID != null && raw.quoteID !== '0' ? asNumber(raw.quoteID) : null),
    nextBillingDate: asNullableString(raw.nextBillingDate),
    paymentStatus: asNullableString(raw.paymentStatus),
  };
}

export async function listTemplates(params?: {
  incrementType?: number;
  limit?: number;
  /** Include inactive templates too — a report over past months can still contain their members. */
  allStatuses?: boolean;
}): Promise<MembershipTemplate[]> {
  const res = await apiClient.get<{ data: unknown }>('/memberships/templates', { params: {
    ...(params?.incrementType != null ? { increment_type: params.incrementType } : {}),
    ...(params?.limit != null ? { limit: params.limit } : {}),
    ...(params?.allStatuses ? { all_statuses: 1 } : {}),
  } });
  return flattenTemplatePayload(res.data.data).map(mapTemplate);
}

export async function getTemplate(templateId: number): Promise<MembershipTemplate> {
  const res = await apiClient.get<{ data: RawRecord }>(`/memberships/templates/${templateId}`);
  return mapTemplate(res.data.data);
}

export async function createTemplate(payload: CreateTemplatePayload): Promise<MembershipTemplate> {
  const res = await apiClient.post<{ data: RawRecord }>('/memberships/templates', payload);
  const createdId = asNumber(res.data.data?.membershipTemplateID);
  return createdId ? getTemplate(createdId) : listTemplates().then((items) => items[0]);
}

export async function updateTemplate(
  templateId: number,
  payload: UpdateTemplatePayload,
): Promise<MembershipTemplate> {
  await apiClient.put(`/memberships/templates/${templateId}`, payload);
  return getTemplate(templateId);
}

export async function deleteTemplate(templateId: number): Promise<void> {
  await apiClient.delete(`/memberships/templates/${templateId}`);
}

export async function addProcedureToTemplate(
  templateId: number,
  payload: AddProcedurePayload,
): Promise<MembershipTemplate> {
  await apiClient.post(`/memberships/templates/${templateId}/procedures`, {
    procSubID: payload.procedureId,
    price: payload.standardPrice,
    effectivePrice: payload.effectivePrice,
  });
  return getTemplate(templateId);
}

export async function removeProcedureFromTemplate(
  templateId: number,
  procedureId: number,
): Promise<MembershipTemplate> {
  await apiClient.delete(`/memberships/templates/${templateId}/procedures/${procedureId}`);
  return getTemplate(templateId);
}

export async function addProductToTemplate(
  templateId: number,
  payload: AddProductPayload,
): Promise<MembershipTemplate> {
  await apiClient.post(`/memberships/templates/${templateId}/products`, {
    name: payload.name,
    quantity: payload.quantity,
    curRate: payload.standardPrice,
    total: payload.effectivePrice,
  });
  return getTemplate(templateId);
}

export async function removeProductFromTemplate(
  templateId: number,
  productId: number,
): Promise<MembershipTemplate> {
  await apiClient.delete(`/memberships/templates/${templateId}/products/${productId}`);
  return getTemplate(templateId);
}

export async function cloneTemplate(templateId: number): Promise<MembershipTemplate> {
  const res = await apiClient.post<{ data: RawRecord }>(`/memberships/templates/${templateId}/clone`);
  const createdId = asNumber(res.data.data?.membershipTemplateID);
  return createdId ? getTemplate(createdId) : mapTemplate(res.data.data);
}

export async function listEnrollments(): Promise<MembershipEnrollment[]> {
  const res = await apiClient.get<{ data: RawRecord[] }>('/memberships/enrollments');
  return (res.data.data ?? []).map(mapEnrollment);
}

export async function enrollPatient(
  patientId: number,
  templateId: number,
): Promise<MembershipEnrollment> {
  const res = await apiClient.post<{ data: RawRecord }>('/memberships/enroll', {
    patientId,
    membershipTemplateId: templateId,
  });
  return mapEnrollment(res.data.data);
}

export async function getPatientMemberships(
  patientId: number,
): Promise<MembershipEnrollment[]> {
  const res = await apiClient.get<{ data: RawRecord[] }>(`/memberships/patients/${patientId}`);
  return (res.data.data ?? []).map(mapEnrollment);
}

export async function cancelEnrollment(
  enrollmentId: number,
  reasonId?: number,
): Promise<MembershipEnrollment> {
  const res = await apiClient.post<{ data: RawRecord }>(
    `/memberships/enrollments/${enrollmentId}/cancel`,
    { reasonId },
  );
  return mapEnrollment(res.data.data);
}

export async function useItem(
  enrollmentId: number,
  itemType: ItemType,
  itemUsageId: number,
  disburseQuantity?: number,
): Promise<EnrollmentMembershipItem> {
  const payload: Record<string, unknown> = { itemType, itemUsageId };
  if (disburseQuantity != null) {
    payload.disburseQuantity = disburseQuantity;
  }

  const res = await apiClient.post<{ data: RawRecord }>(
    `/memberships/enrollments/${enrollmentId}/use`,
    payload,
  );
  return mapMembershipBlock(res.data.data);
}

export async function returnItem(
  enrollmentId: number,
  itemType: ItemType,
  itemUsageId: number,
): Promise<EnrollmentMembershipItem> {
  const res = await apiClient.post<{ data: RawRecord }>(
    `/memberships/enrollments/${enrollmentId}/return`,
    { itemType, itemUsageId },
  );
  return mapMembershipBlock(res.data.data);
}

export async function deleteItem(
  enrollmentId: number,
  itemType: ItemType,
  itemUsageId: number,
): Promise<EnrollmentMembershipItem> {
  const res = await apiClient.post<{ data: RawRecord }>(
    `/memberships/enrollments/${enrollmentId}/delete`,
    { itemType, itemUsageId },
  );
  return mapMembershipBlock(res.data.data);
}

export async function listCancellationReasons(): Promise<CancellationReason[]> {
  const res = await apiClient.get<{ data: RawRecord[] }>('/memberships/cancellation-reasons');
  return (res.data.data ?? []).map((raw) => ({
    reasonId: asNumber(raw.reasonId ?? raw.membershipReasonID ?? raw.membershipCancelReasonID),
    accountId: asNumber(raw.accountId ?? raw.accountID),
    reason: asString(raw.reason ?? raw.title),
    active: raw.active == null ? true : Boolean(raw.active),
  }));
}

// ─── Enrollment detail & management ──────────────────────────────────────────

export interface EnrollmentPaymentMethod {
  id: number;
  title: string;
  lastFour: string;
  firstSix: string;
  expireDate: string | null;
}

export interface EnrollmentMembershipItem {
  membershipId: number;
  templateId: number | null;
  title: string;
  procedureItems: MembershipItemUsage[];
  productItems: MembershipItemUsage[];
}

export interface EnrollmentDetail {
  recurringPaymentId: number;
  patientId: number;
  patientName: string;
  amount: number;
  notes: string;
  nextChargeDate: string | null;
  pauseUntilDate: string | null;
  endDate: string | null;
  createDate: string;
  membershipsText: string;
  memberships: EnrollmentMembershipItem[];
  paymentMethod: EnrollmentPaymentMethod | null;
}

export interface PaymentHistoryRecord {
  id: number;
  paymentId: number;
  paymentDate: string | null;
  paymentUntilDate: string | null;
  status: string;
  amount: number;
  memo: string;
  isVoided: boolean;
}

export interface UpdateEnrollmentPayload {
  amount?: number;
  notes?: string;
  nextChargeDate?: string;
}

function mapEnrollmentDetail(raw: RawRecord): EnrollmentDetail {
  const pm = raw.paymentMethod as RawRecord | null;
  return {
    recurringPaymentId: asNumber(raw.recurringPaymentId),
    patientId: asNumber(raw.patientId),
    patientName: asString(raw.patientName),
    amount: asNumber(raw.amount),
    notes: asString(raw.notes),
    nextChargeDate: asNullableString(raw.nextChargeDate),
    pauseUntilDate: asNullableString(raw.pauseUntilDate),
    endDate: asNullableString(raw.endDate),
    createDate: asString(raw.createDate),
    membershipsText: asString(raw.membershipsText),
    memberships: Array.isArray(raw.memberships)
      ? (raw.memberships as RawRecord[]).map((m) => ({
          membershipId: asNumber(m.membershipId),
          // The API ships this as `membershipTemplateID`, and sends 0 when the
          // membership was created without a template.
          templateId: asNumber(m.membershipTemplateID) || null,
          title: asString(m.title),
          procedureItems: Array.isArray(m.procedureItems)
            ? (m.procedureItems as RawRecord[]).map((item) => mapItemUsage(item, asNumber(m.membershipId), 'procedure'))
            : [],
          productItems: Array.isArray(m.productItems)
            ? (m.productItems as RawRecord[]).map((item) => mapItemUsage(item, asNumber(m.membershipId), 'product'))
            : [],
        }))
      : [],
    paymentMethod: pm
      ? {
          id: asNumber(pm.id),
          title: asString(pm.title),
          lastFour: asString(pm.lastFour),
          firstSix: asString(pm.firstSix),
          expireDate: asNullableString(pm.expireDate),
        }
      : null,
  };
}

export async function getEnrollmentDetail(recurringId: number): Promise<EnrollmentDetail> {
  const res = await apiClient.get<{ data: RawRecord }>(`/memberships/enrollments/${recurringId}`);
  return mapEnrollmentDetail(res.data.data);
}

export async function updateEnrollment(
  recurringId: number,
  payload: UpdateEnrollmentPayload,
): Promise<void> {
  await apiClient.put(`/memberships/enrollments/${recurringId}`, payload);
}

export async function freezeEnrollment(recurringId: number, pauseUntilDate: string): Promise<void> {
  await apiClient.post(`/memberships/enrollments/${recurringId}/freeze`, { pauseUntilDate });
}

export async function unfreezeEnrollment(recurringId: number): Promise<void> {
  await apiClient.post(`/memberships/enrollments/${recurringId}/unfreeze`);
}

export interface AddEnrollmentItemPayload {
  membershipId?: number;
  itemType: 'product' | 'procedure';
  productId?: number;
  procedureId?: number;
  quantity?: number;
  expireDate?: string;
}

export interface ProcedureSearchResult {
  id: number;
  name: string;
  displayName: string;
  price: number;
}

export interface ProductSearchResult {
  id: number;
  name: string;
  description: string;
  price: number;
  quantityRemaining: number | null;
  categoryTitle: string | null;
}

export async function searchProcedures(search: string): Promise<ProcedureSearchResult[]> {
  const res = await apiClient.get<{ data: RawRecord[] }>('/procedures', { params: { search } });
  return (res.data.data ?? []).map((raw) => ({
    id: asNumber(raw.procedureSpecificID),
    name: asString(raw.name),
    displayName: asString(raw.display_name ?? raw.name),
    price: asNumber(raw.feeToPatient ?? raw.fee ?? 0),
  }));
}

export async function searchProducts(search: string): Promise<ProductSearchResult[]> {
  // limit 50 (endpoint caps at 100): a search like "iv therapy" matches every
  // product in that category via the category-title match, so a low cap would
  // silently truncate valid results before the one the user wants. 50 gives
  // ample headroom for a debounced typeahead without paginating.
  const res = await apiClient.get<{ data?: RawRecord[] }>('/products/grouped', { params: { search, limit: 50, require_product_type: 1 } });
  return (res.data.data ?? [])
    .map((raw) => {
      const groupData = raw.groupData as RawRecord | undefined;
      if (!groupData) return null;

      const category = groupData.category as RawRecord | undefined;
      return {
        id: asNumber(groupData.quoteItemCostID),
        name: asString(groupData.strItem),
        description: asString(groupData.strDescription),
        price: asNumber(groupData.curRate),
        quantityRemaining: groupData.quantityRemaining == null ? null : asNumber(groupData.quantityRemaining),
        categoryTitle: category ? asNullableString(category.title) : null,
      };
    })
    .filter((item): item is ProductSearchResult => item !== null);
}

export async function addEnrollmentItem(
  recurringId: number,
  payload: AddEnrollmentItemPayload,
): Promise<void> {
  await apiClient.post(`/memberships/enrollments/${recurringId}/items`, payload);
}

export async function getPaymentHistory(recurringId: number): Promise<PaymentHistoryRecord[]> {
  const res = await apiClient.get<{ data: RawRecord[] }>(
    `/memberships/enrollments/${recurringId}/payment-history`,
  );
  return (res.data.data ?? []).map((raw) => ({
    id: asNumber(raw.id),
    paymentId: asNumber(raw.paymentId),
    paymentDate: asNullableString(raw.paymentDate),
    paymentUntilDate: asNullableString(raw.paymentUntilDate),
    status: asString(raw.status),
    amount: asNumber(raw.amount),
    memo: asString(raw.memo),
    isVoided: Boolean(raw.isVoided),
  }));
}

// ─── Recurring Payment Profile (patient tab rows from payments_recurring_authnet) ─

export interface RecurringPaymentRow {
  paymentsRecurringAuthnetID: number;
  createDate: string;
  patientID: number;
  planID: number | null;
  planDisplayID: string | null;
  quoteID: string | null;
  strPayType: string;
  strPayMethod: string;
  curAmount: number;
  incrementType: string;
  status: string;
  paymentStatusDisplay: string;
  nextBillingDate: string | null;
  membershipTitle: string | null;
  paymentThroughDate: string | null;
  reactivationThroughDate: string | null;
  endDate: string | null;
  firstBillingDate: string | null;
}

export async function getPatientRecurringProfiles(patientId: number): Promise<RecurringPaymentRow[]> {
  const res = await apiClient.get<{ data: RawRecord[] }>(`/memberships/patients/${patientId}`);
  return (res.data.data ?? []).map((raw) => ({
    paymentsRecurringAuthnetID: asNumber(raw.paymentsRecurringAuthnetID),
    createDate: asString(raw.createDate),
    patientID: asNumber(raw.patientID),
    planID: raw.planID != null ? asNumber(raw.planID) : null,
    planDisplayID: asNullableString(raw.planDisplayID),
    quoteID: asNullableString(raw.quoteID),
    strPayType: asString(raw.strPayType),
    strPayMethod: asString(raw.strPayMethod),
    curAmount: asNumber(raw.curAmount),
    incrementType: asString(raw.incrementType, '1'),
    status: asString(raw.status, 'Active'),
    paymentStatusDisplay: asString(raw.paymentStatusDisplay, 'Paid'),
    nextBillingDate: asNullableString(raw.nextBillingDate),
    membershipTitle: asNullableString(raw.membershipTitle),
    paymentThroughDate: asNullableString(raw.paymentThroughDate),
    reactivationThroughDate: asNullableString(raw.reactivationThroughDate),
    endDate: asNullableString(raw.endDate),
    firstBillingDate: asNullableString(raw.firstBillingDate),
  }));
}

// ─── Expanded Recurring Detail (all fields for detail modal) ─────────────────

export interface RecurringGiftCard {
  paymentsRecurringGiftCardID: number;
  giftCardID: number;
  giftCardRefID: string;
  giftCardTitle: string;
  redeemedAmount: number;
}

export interface RecurringLoan {
  paymentLoanId: number;
  loanId: number;
  paymentAmount: number;
  loanType: string;
  loanAmount: number;
  paidAmount: number;
  remainingBalance: number;
  loanStatus: string;
  createDate: string | null;
}

export interface RecurringChargeResult {
  recurringId: number;
  recordId?: number;
  status: string;
  paymentId: number;
  paymentTransactionAuthnetId?: number;
}

function mapRecurringChargeResult(raw: RawRecord | undefined): RecurringChargeResult | undefined {
  if (!raw) return undefined;

  return {
    recurringId: asNumber(raw.recurringId),
    recordId: raw.recordId == null ? undefined : asNumber(raw.recordId),
    status: asString(raw.status),
    paymentId: asNumber(raw.paymentId),
    paymentTransactionAuthnetId: raw.paymentTransactionAuthnetId == null
      ? undefined
      : asNumber(raw.paymentTransactionAuthnetId),
  };
}

export interface RecurringPaymentMethod {
  id: number;
  token: string;
  title: string;
  lastFour: string;
  firstSix: string;
  expireDate: string | null;
  pmEnumID?: string;
  paymentMethodID?: number;
  /** False when the card was removed from the patient's profile. The recurring payment still charges it. */
  active?: boolean;
}

export interface RecurringDetailData {
  recurringPaymentId: number;
  patientId: number;
  patientName: string;
  amount: number;
  notes: string;
  createDate: string;
  membershipsText: string;

  incrementType: string;
  chargeDay: string;
  declined: string;
  strPayType: string;
  strPayMethod: string;
  planID: number | null;
  planDisplayID: string | null;
  quoteID: string | null;
  facilityID: number;
  accountMerchantId: string | null;
  paymentMethodToken: string;

  currentRecordStatus: string;
  currentPaymentStatus: string;
  firstBillingDate: string;
  nextBillingDate: string | null;
  paymentThroughDate: string | null;
  frozenFromDate: string | null;
  frozenToDate: string | null;
  endDate: string | null;
  pauseUntilDate: string | null;
  nextBillingMinDate: string | null;
  nextBillingMaxDate: string | null;

  cancelReasonId: string | null;
  cancelOtherReason: string | null;
  /** When the cancellation was submitted (not the effective stop date). */
  cancelRequestedDate: string | null;
  /** Staff member who cancelled. 0 / '' for pre-tracking or system auto-cancels. */
  canceledByUserId: number;
  canceledByName: string;
  giftCards: RecurringGiftCard[];
  loans: RecurringLoan[];
  retryPaymentId: number | null;

  paymentMethod: RecurringPaymentMethod | null;
  allPaymentMethods: RecurringPaymentMethod[];
  memberships: EnrollmentMembershipItem[];

  requiredDuration: number;
  cancelDuration: number;
  reactivationThroughDate: string | null;
  membershipAgreement: MembershipAgreementAcceptance | null;
}

export interface MembershipAgreementAcceptance {
  patientMembershipAgreementID: number;
  membershipAgreementID: number;
  version: number;
  agreedDate: string;
  signedName: string;
  signedBirthDate: string;
  title: string;
  bodyHtml: string;
  groupID: number;
  effectiveDate: string | null;
}

function mapRecurringDetail(raw: RawRecord): RecurringDetailData {
  return {
    recurringPaymentId: asNumber(raw.recurringPaymentId),
    patientId: asNumber(raw.patientId),
    patientName: asString(raw.patientName),
    amount: asNumber(raw.amount),
    notes: asString(raw.notes),
    createDate: asString(raw.createDate),
    membershipsText: asString(raw.membershipsText),
    incrementType: asString(raw.incrementType, '1'),
    chargeDay: asString(raw.chargeDay),
    declined: asString(raw.declined, '0'),
    strPayType: asString(raw.strPayType),
    strPayMethod: asString(raw.strPayMethod),
    planID: raw.planID != null ? asNumber(raw.planID) : null,
    planDisplayID: asNullableString(raw.planDisplayID),
    quoteID: asNullableString(raw.quoteID),
    facilityID: asNumber(raw.facilityID),
    accountMerchantId: asNullableScalarString(raw.accountMerchantId),
    paymentMethodToken: asString(raw.paymentMethodToken),
    currentRecordStatus: asString(raw.currentRecordStatus),
    currentPaymentStatus: asString(raw.currentPaymentStatus),
    firstBillingDate: asString(raw.firstBillingDate),
    nextBillingDate: asNullableString(raw.nextBillingDate),
    paymentThroughDate: asNullableString(raw.paymentThroughDate),
    frozenFromDate: asNullableString(raw.frozenFromDate),
    frozenToDate: asNullableString(raw.frozenToDate),
    endDate: asNullableString(raw.endDate),
    pauseUntilDate: asNullableString(raw.pauseUntilDate),
    nextBillingMinDate: asNullableString(raw.nextBillingMinDate),
    nextBillingMaxDate: asNullableString(raw.nextBillingMaxDate),
    cancelReasonId: asNullableScalarString(raw.cancelReasonId),
    cancelOtherReason: asNullableString(raw.cancelOtherReason),
    cancelRequestedDate: asNullableString(raw.cancelRequestedDate),
    canceledByUserId: asNumber(raw.canceledByUserId),
    canceledByName: asString(raw.canceledByName),
    giftCards: Array.isArray(raw.giftCards)
      ? (raw.giftCards as RawRecord[]).map((gc) => ({
          paymentsRecurringGiftCardID: asNumber(gc.paymentsRecurringGiftCardID),
          giftCardID: asNumber(gc.giftCardID),
          giftCardRefID: asString(gc.giftCardRefID),
          giftCardTitle: asString(gc.giftCardTitle),
          redeemedAmount: asNumber(gc.redeemedAmount),
        }))
      : [],
    loans: Array.isArray(raw.loans)
      ? (raw.loans as RawRecord[]).map((loan) => ({
          paymentLoanId: asNumber(loan.paymentLoanId),
          loanId: asNumber(loan.loanId),
          paymentAmount: asNumber(loan.paymentAmount),
          loanType: asString(loan.loanType),
          loanAmount: asNumber(loan.loanAmount),
          paidAmount: asNumber(loan.paidAmount),
          remainingBalance: asNumber(loan.remainingBalance ?? loan.balance),
          loanStatus: asString(loan.loanStatus),
          createDate: asNullableString(loan.createDate),
        }))
      : [],
    retryPaymentId: raw.retryPaymentId != null ? asNumber(raw.retryPaymentId) : null,
    paymentMethod: raw.paymentMethod
      ? {
          id: asNumber((raw.paymentMethod as RawRecord).id),
          token: asString((raw.paymentMethod as RawRecord).token),
          title: asString((raw.paymentMethod as RawRecord).title),
          lastFour: asString((raw.paymentMethod as RawRecord).lastFour),
          firstSix: asString((raw.paymentMethod as RawRecord).firstSix),
          expireDate: asNullableString((raw.paymentMethod as RawRecord).expireDate),
          pmEnumID: asString((raw.paymentMethod as RawRecord).pmEnumID, '0'),
          paymentMethodID: asNumber((raw.paymentMethod as RawRecord).paymentMethodID ?? 0),
          active: (raw.paymentMethod as RawRecord).active !== false,
        }
      : null,
    allPaymentMethods: (() => {
      const src = raw.allPaymentMethods;
      const arr: RawRecord[] = Array.isArray(src)
        ? src as RawRecord[]
        : (src && typeof src === 'object' ? Object.values(src) as RawRecord[] : []);
      return arr.map((pm) => ({
        id: asNumber(pm.id),
        token: asString(pm.token),
        title: asString(pm.title),
        lastFour: asString(pm.lastFour),
        firstSix: asString(pm.firstSix),
        expireDate: asNullableString(pm.expireDate),
        pmEnumID: asString(pm.pmEnumID, '0'),
        paymentMethodID: asNumber(pm.paymentMethodID ?? 0),
      }));
    })(),
    memberships: Array.isArray(raw.memberships)
      ? (raw.memberships as RawRecord[]).map(mapMembershipBlock)
      : [],
    requiredDuration: asNumber(raw.requiredDuration ?? 0),
    cancelDuration: asNumber(raw.cancelDuration ?? 0),
    reactivationThroughDate: asNullableString(raw.reactivationThroughDate),
    membershipAgreement: raw.membershipAgreement
      ? (() => {
          const ma = raw.membershipAgreement as RawRecord;
          return {
            patientMembershipAgreementID: asNumber(ma.patientMembershipAgreementID),
            membershipAgreementID: asNumber(ma.membershipAgreementID),
            version: asNumber(ma.version),
            agreedDate: asString(ma.agreedDate),
            signedName: asString(ma.signedName),
            signedBirthDate: asString(ma.signedBirthDate),
            title: asString(ma.title),
            bodyHtml: asString(ma.bodyHtml),
            groupID: asNumber(ma.groupID),
            effectiveDate: asNullableString(ma.effectiveDate),
          };
        })()
      : null,
  };
}

export async function getRecurringDetail(recurringId: number): Promise<RecurringDetailData> {
  const res = await apiClient.get<{ data: RawRecord }>(`/memberships/enrollments/${recurringId}`);
  return mapRecurringDetail(res.data.data);
}

// ─── Cancel / Reactivate / Freeze actions ────────────────────────────────────

export async function cancelRecurringPayment(
  recurringId: number,
  cancelReason?: string,
  otherReason?: string,
  cancelDate?: string,
): Promise<void> {
  await apiClient.post(`/memberships/enrollments/${recurringId}/cancel-recurring`, {
    cancelReason: cancelReason ?? '0',
    otherReason: otherReason ?? '',
    ...(cancelDate ? { cancelDate } : {}),
  });
}

export async function adjustCancelDate(
  recurringId: number,
  cancelDate: string,
): Promise<void> {
  await apiClient.post(`/memberships/enrollments/${recurringId}/adjust-cancel-date`, {
    cancelDate,
  });
}

export async function reactivateRecurring(recurringId: number): Promise<void> {
  await apiClient.post(`/memberships/enrollments/${recurringId}/reactivate`);
}

export interface FreezeRecord {
  paymentsRecurringRecordID: number;
  paymentDate: string;
  paymentUntilDate: string;
  paymentAmount: number;
  status: string;
}

export async function getFreezeRecords(recurringId: number): Promise<FreezeRecord[]> {
  const res = await apiClient.get<{ data: RawRecord[] }>(
    `/memberships/enrollments/${recurringId}/freeze-records`,
  );
  return (res.data.data ?? []).map((raw) => ({
    paymentsRecurringRecordID: asNumber(raw.paymentsRecurringRecordID),
    paymentDate: asString(raw.paymentDate),
    paymentUntilDate: asString(raw.paymentUntilDate),
    paymentAmount: asNumber(raw.paymentAmount),
    status: asString(raw.status, 'active'),
  }));
}

export async function saveFreezeRecords(
  recurringId: number,
  records: { paymentsRecurringRecordID: number; status: string; paymentAmount: number }[],
): Promise<void> {
  await apiClient.post(`/memberships/enrollments/${recurringId}/freeze-records`, { records });
}

export async function unfreezeAndCharge(
  recurringId: number,
): Promise<{ chargeNow: boolean; chargeResult?: RecurringChargeResult }> {
  const res = await apiClient.post<{ data: { chargeNow?: boolean; chargeResult?: RawRecord } }>(
    `/memberships/enrollments/${recurringId}/unfreeze`,
  );
  return {
    chargeNow: Boolean(res.data.data?.chargeNow),
    chargeResult: mapRecurringChargeResult(res.data.data?.chargeResult as RawRecord | undefined),
  };
}

export async function saveRecurringPayment(
  recurringId: number,
  payload: Record<string, unknown>,
): Promise<{ success: boolean; recurringId: number; chargeNow: boolean; chargeResult?: RecurringChargeResult }> {
  const res = await apiClient.put<{ data: RawRecord }>(`/memberships/enrollments/${recurringId}`, payload);
  return {
    success: Boolean(res.data.data?.success),
    recurringId: asNumber(res.data.data?.recurringId),
    chargeNow: Boolean(res.data.data?.chargeNow),
    chargeResult: mapRecurringChargeResult(res.data.data?.chargeResult as RawRecord | undefined),
  };
}

export async function retryRecurringPayment(recurringId: number): Promise<RecurringChargeResult> {
  const res = await apiClient.post<{ data: RawRecord }>(`/memberships/enrollments/${recurringId}/retry-payment`);
  return mapRecurringChargeResult(res.data.data) as RecurringChargeResult;
}

export async function chargeRecurringNow(recurringId: number): Promise<RecurringChargeResult> {
  const res = await apiClient.post<{ data: RawRecord }>(`/memberships/enrollments/${recurringId}/charge-now`);
  return mapRecurringChargeResult(res.data.data) as RecurringChargeResult;
}
